import type {DoppeActionType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {
    BymoPagePropsContentSettingsMetadata,
    DoppeActionPriceData,
    DoppeDtoStoreProduct
} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    doppeDtoProductGetMediaItems,
    doppeDtoStoreProductPriceDataConvertToActionPriceData,
    DoppeExternalProductProvider,
    doppeExternalProductProviderResolveBusinessEntityReference
} from '../../client-server-common';
import {DoppeSupportedWixTpas} from '../../client-server-common/types/doppe-supported-wix-tpas';
import {DoppeDtoUserMetaSiteType} from '../../client-server-common/types/doppe-dto-user';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {
    wixMediaResourceReplaceExternalImage,
    wixProductMediaTryConvertToWixMediaResource
} from '@wix/devzai-common-wix';
import {DoppeActionTypeProductViewerLang} from './doppe-action-type-product-viewer.lang';
import {
    arrayRemoveNullValues,
    asArray,
    HideableValue,
    hideableValueCreateHidden,
    hideableValueGetValue,
    hideableValueIsVisibleAndNotEqualValue,
    hideableValueModifyValue,
    immerProduce,
    OverridableValue,
    overridableValueCreateOriginal,
    overridableValueGetOverriddenValue,
    overridableValueGetValue,
    overridableValueIsOriginal,
    RichTextContent,
    stringIsNotNullableOrWhiteSpace,
    Values
} from '@wix/devzai-utils-common';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    doppeActionCtaButtonTypeGetDefaultSettings,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonCreate
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';

export const DoppeActionTypeProductAdditionalSectionsDisplay = {
    None: 'None',
    Start: 'Start',
    End: 'End',
} as const;

export type DoppeActionTypeProductAdditionalSectionsDisplay = Values<typeof DoppeActionTypeProductAdditionalSectionsDisplay>;

export namespace DoppeActionTypeProduct {

    export interface ProductSettings {
        productPageLink: string;
        priceData: HideableValue<OverridableValue<DoppeActionPriceData>>;
        productTitle: HideableValue<OverridableValue<string>>;
        description: HideableValue<OverridableValue<RichTextContent>>;
        productImage: OverridableValue<WixMediaResource[] | WixMediaResource | null>;
        couponCode: HideableValue<string>;
        ribbon: HideableValue<OverridableValue<string>>;
        productProvider: DoppeExternalProductProvider | null;
        productProviderId: string | null;
    }

    export interface ActionSettings extends
        DoppeActionTypeProduct.ProductSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        displayAdditionalSections: DoppeActionTypeProductAdditionalSectionsDisplay;
    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        productButtonText?: string;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeProductActionContentSettingsMetadata = {
    productTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
    productPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    productImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    priceData: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.PriceData, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeProduct.ActionSettings>

export const doppeActionTypeProductSettingsMetadata = {
    contentSettingsMetadata: doppeActionTypeProductActionContentSettingsMetadata,
    supportsActionCtaButtons: {
        customCtaButtonTypes: {
            [DoppeActionCTAType.BuyNow]: true,
            [DoppeActionCTAType.AddToCart]: true
        }
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
            couponCode: true,
            description: true,
            priceData: true,
            productTitle: true,
            ribbon: true
        },
    bindableContentSettingsNames: {
            description: true,
            priceData: true,
            productImage: true,
            productTitle: true,
            ribbon: true
        }
} satisfies DoppeActionType.SettingsMetadata<DoppeActionTypeProduct.ActionSettings>

export const doppeActionTypeProduct = doppeActionTypeDefineMetadata<DoppeActionTypeProduct.ActionSettings, DoppeActionTypeProduct.PartialProps>({
    id: 'io.bymo.action/product',
    resolveActionSettings: action => {

        const {
            productButtonText = DoppeActionTypeProductViewerLang.defaultTexts.productButtonText,
            productPageLink = '',
            ctaButton,
            ctaButtons,
            sections,
            displayAdditionalSections = DoppeActionTypeProductAdditionalSectionsDisplay.None,
            priceData = hideableValueCreateHidden({actualPrice: DoppeActionTypeProductViewerLang.defaultTexts.price, discountPrice: ''}),
            ...restSettings
        } = action.settings;

        const resolvedSettings = {
            productPageLink: productPageLink,
            displayAdditionalSections: displayAdditionalSections,
            priceData: hideableValueModifyValue(priceData, overridableValue => {
                if (overridableValueIsOriginal(overridableValue)) {
                    return overridableValue;
                }
                const innerValue = overridableValueGetValue(overridableValue, {actualPrice: '', discountPrice: ''});
                const resolved = {...innerValue, actualPrice: innerValue.actualPrice ?? '', discountPrice: innerValue.discountPrice ?? ''};
                return resolved;
            }),
            productTitle: hideableValueCreateHidden(DoppeActionTypeProductViewerLang.defaultTexts.productTitle),
            description: DoppeActionTypeProductViewerLang.defaultTexts.description,
            productButtonText: productButtonText,
            productImage: null,
            couponCode: hideableValueCreateHidden('CouponCode'),
            ribbon: hideableValueCreateHidden('SALE'),
            productProvider: null,
            productProviderId: null,
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: productButtonText,
                showButton: true,
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
                ctaButtons: ctaButtons,
            }, true),
            ...restSettings
        };

        return resolvedSettings;
    },
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    actionSettingsMetadata: doppeActionTypeProductSettingsMetadata,
    actionListItemSettingsMetadata: false,
    resolveUsedMedia: actionSettings => {
        const productImage = overridableValueGetValue(actionSettings.productImage);
        return arrayRemoveNullValues(asArray(productImage ?? null));
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const storeProduct = actionSettings.productProvider && actionSettings.productProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalProductProviderResolveBusinessEntityReference(actionSettings.productProvider!, actionSettings.productProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoStoreProduct : null;
        })() : null;

        const productImage = overridableValueGetOverriddenValue(
            actionSettings.productImage,
            storeProduct ? doppeDtoProductGetMediaItems(storeProduct) : null
        );
        return productImage ? asArray(productImage)[0] ?? null : null;
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.productPageLink ?? null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveCouponData: (actionSettings, bymoPageContextProps) => {
        const storeProduct = actionSettings.productProvider && actionSettings.productProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalProductProviderResolveBusinessEntityReference(actionSettings.productProvider!, actionSettings.productProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoStoreProduct : null;
        })() : null;

        const description = hideableValueModifyValue(
            actionSettings.description,
            value => overridableValueGetOverriddenValue(value, storeProduct?.descriptionPlainText ?? '')
        );
        const productTitle = hideableValueModifyValue(
            actionSettings.productTitle,
            value => overridableValueGetOverriddenValue(value, storeProduct?.name ?? '')
        );
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: hideableValueGetValue(actionSettings.couponCode),
            couponDescription: hideableValueIsVisibleAndNotEqualValue(description, '') ? description : '',
            couponTitle: hideableValueIsVisibleAndNotEqualValue(productTitle, '') ? productTitle : '',
        }
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.productProvider !== null && actionSettings.productProviderId !== null ?
                doppeExternalProductProviderResolveBusinessEntityReference(
                    actionSettings.productProvider,
                    actionSettings.productProviderId
                ) :
                null
        ])
    },
    resolveActionEntityBinding: actionSettings => {
        const {productProvider, productProviderId} = actionSettings;
        return productProvider !== null && productProviderId !== null
            ? doppeExternalProductProviderResolveBusinessEntityReference(productProvider, productProviderId)
            : null;
    },
    resolveSettingsFromBoundEntityReference: (ref) => {
        switch (ref.businessEntity) {
            case DoppeBusinessEntity.WixProduct:
                return {productProvider: DoppeExternalProductProvider.Wix, productProviderId: ref.id};
            case DoppeBusinessEntity.HoppProduct:
                return {productProvider: DoppeExternalProductProvider.Hopp, productProviderId: ref.id};
            default:
                return {};
        }
    },
    getSupportedBoundEntitiesByTpa: () => ({
        [DoppeDtoUserMetaSiteType.LinkedMetaSite]: {
            [DoppeSupportedWixTpas.StoresNew]: [DoppeBusinessEntity.WixProduct],
        },
        [DoppeDtoUserMetaSiteType.CreatorsMetaSite]: {
            [DoppeSupportedWixTpas.StoresNew]: [DoppeBusinessEntity.HoppProduct],
        },
    }),
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            const productImage = overridableValueGetValue(actionSettings.productImage);
            if (productImage) {
                const images = asArray(productImage);
                actionSettings.productImage = images.map(image => {
                    return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                })
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.productPageLink = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {productProvider, productProviderId} = actionSettings;
        if (productProvider !== null && productProviderId !== null) {
            const boundProduct = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalProductProviderResolveBusinessEntityReference(productProvider, productProviderId)
            );
            if (boundProduct && !doppeBusinessEntityDtoIsNotFound(boundProduct)) {
                return {
                    ...actionSettings,
                    ...doppeActionTypeProductResolveSettingsFromBoundEntity(actionSettings, boundProduct as DoppeDtoStoreProduct),
                };
            }
        }
        return actionSettings;
    },
});

export function doppeActionTypeProductResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeProduct.ProductSettings,
    storeProduct: DoppeDtoStoreProduct
)  {
    const entityPriceData = doppeDtoStoreProductPriceDataConvertToActionPriceData(storeProduct.price);
    const entityProductImages = storeProduct.media?.items ?
        arrayRemoveNullValues(storeProduct.media.items.map(item => wixProductMediaTryConvertToWixMediaResource(item))) : null;

    return {
        ...actionSettings,
        priceData: hideableValueModifyValue(
            actionSettings.priceData,
            value => overridableValueGetOverriddenValue(value, entityPriceData)
        ),
        productTitle: hideableValueModifyValue(
            actionSettings.productTitle,
            value => overridableValueGetOverriddenValue(value, storeProduct.name)
        ),
        description: hideableValueModifyValue(
            actionSettings.description,
            value => overridableValueGetOverriddenValue(value, storeProduct.descriptionPlainText)
        ),
        productImage: overridableValueGetOverriddenValue(
            actionSettings.productImage,
            entityProductImages
        ),
        ribbon: hideableValueModifyValue(
            actionSettings.ribbon,
            value => overridableValueGetOverriddenValue(value, storeProduct.ribbon ?? '')
        ),
        productPageLink: stringIsNotNullableOrWhiteSpace(storeProduct.productPageUrl) ?
            storeProduct.productPageUrl : 'about:blank',
    } satisfies DoppeActionTypeProduct.ProductSettings
}

export function convertDoppeWixProductToProductSettingsWithDefaultTexts(options: {
    product: DoppeDtoStoreProduct,
    productSettings?: DoppeActionTypeProduct.ActionSettings,
    pageContentLocales: DoppeAppPageContentLocales,
    /** The provider of the product. Defaults to Wix. */
    productProvider?: DoppeExternalProductProvider,
}) {

    const {
        product,
        productSettings,
        pageContentLocales,
        productProvider = DoppeExternalProductProvider.Wix,
    } = options;

    const productImages = product.media?.items ?
        arrayRemoveNullValues(product.media?.items.map(item => wixProductMediaTryConvertToWixMediaResource(item))) : null;

    const productTitle = product?.name;

    return {
        ribbon: overridableValueCreateOriginal(product.ribbon ?? ''),
        couponCode: hideableValueCreateHidden(pageContentLocales["product.actionSettings.couponCode"]),
        sections: [],
        displayAdditionalSections: DoppeActionTypeProductAdditionalSectionsDisplay.End,
        ctaButtons: [doppeActionMultipleCTAButtonCreate({
            text: pageContentLocales["product.actionSettings.productButtonText"],
            isRequired: false,
            settings: doppeActionCtaButtonTypeGetDefaultSettings(DoppeActionCTAType.Link, true)
        })],
        ...productSettings,
        productImage: overridableValueCreateOriginal(productImages),
        productPageLink: product.productPageUrl ?? productSettings?.productPageLink ?? '',
        priceData: overridableValueCreateOriginal(doppeDtoStoreProductPriceDataConvertToActionPriceData(product.price)),
        productTitle: overridableValueCreateOriginal(productTitle ?? pageContentLocales["product.actionSettings.title"]),
        description: overridableValueCreateOriginal(product.descriptionPlainText ?? pageContentLocales["product.actionSettings.description"]),
        productProvider: productProvider,
        productProviderId: product.id,
    } satisfies DoppeActionTypeProduct.ActionSettings
}

export function convertDoppeWixProductToProductSettings(options: {
    product: DoppeDtoStoreProduct, productSettings?: DoppeActionTypeProduct.ActionSettings, productProvider?: DoppeExternalProductProvider
}) {

    const {
        product,
        productSettings,
        productProvider = DoppeExternalProductProvider.Wix,
    } = options;

    const productImages = product.media?.items ?
        arrayRemoveNullValues(product.media?.items.map(item => wixProductMediaTryConvertToWixMediaResource(item))) : null;

    const productTitle = product?.name;

    return {
        ribbon: overridableValueCreateOriginal(product.ribbon ?? ''),
        couponCode: hideableValueCreateHidden(''),
        sections: [],
        displayAdditionalSections: DoppeActionTypeProductAdditionalSectionsDisplay.End,
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: [doppeActionMultipleCTAButtonCreate({
                text: '',
                isRequired: false,
                settings: doppeActionCtaButtonTypeGetDefaultSettings(DoppeActionCTAType.Link, true)
            })],
        }, false),
        ...productSettings,
        productImage: overridableValueCreateOriginal(productImages),
        productPageLink: product.productPageUrl ?? productSettings?.productPageLink ?? '',
        priceData: overridableValueCreateOriginal(doppeDtoStoreProductPriceDataConvertToActionPriceData(product.price)),
        productTitle: overridableValueCreateOriginal(productTitle),
        description: overridableValueCreateOriginal(product.descriptionPlainText),
        productProvider: productProvider,
        productProviderId: product.id,
    } satisfies DoppeActionTypeProduct.ActionSettings
}

/**
 * Checks whether any overridable field in the given product settings
 * has been explicitly marked as original (i.e., already migrated).
 */
export function doppeActionTypeProductHasAnyOriginalOverridableField(settings: DoppeActionTypeProduct.ProductSettings): boolean {
    return overridableValueIsOriginal(settings.productImage) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.priceData)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.productTitle)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.description)) ||
        overridableValueIsOriginal(hideableValueGetValue(settings.ribbon));
}

/**
 * Migrates existing connected product settings to use OverridableValue.
 * For existing connected products (productProvider !== null) that haven't been migrated yet,
 * marks priceData as "original" to preserve the current behavior where price
 * is always taken from the bound entity.
 */
export function doppeActionTypeProductMigrateOverridableValues<T extends DoppeActionTypeProduct.ProductSettings>(settings: T): T {
    if (settings.productProvider !== null && !doppeActionTypeProductHasAnyOriginalOverridableField(settings)) {
        return {
            ...settings,
            priceData: hideableValueModifyValue(
                settings.priceData,
                value => overridableValueCreateOriginal(overridableValueGetValue(value))
            ),
        };
    }

    return settings;
}